import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { ElementSaveOptions } from '@/components/ElementDetailV2'
import { StructureObjectDto } from '@/endpoints/models'
import { useAppDispatch, useAppStore } from '@/hooks'
import { useDetailTabContext } from '@/pages/User/pages/Home/components/DetailTab'
import { useSelectedBranch } from '@/pages/User/pages/Home/components/TreePanel/TreePanelContext'
import { mddApi } from '@/rtkApi/mddApi'
import type { CustomAttribute } from '@/rtkApi/mddApiSliceGeneratedV2'
import {
	ObjectSettings,
	SourceItem,
	Status,
	ViewColumn,
	ViewOverview,
	ViewPermission,
	ViewSourcesData,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import { mddApiV2 } from '@/rtkApi/mddApiV2'
import {
	useAddViewColumnMutation,
	useAddViewSourceItemMutation,
	useDeleteViewPermissionMutation,
	useDeleteViewSourceItemMutation,
	useGetStructureDetailQuery,
	useGetViewColumnsQuery,
	useGetViewOverviewQuery,
	useGetViewPermissionsQuery,
	useGetViewSourcesDataQuery,
	useGetViewSourcesQuery,
	useSetViewPermissionMutation,
	useUpdateViewColumn1Mutation,
	useUpdateViewColumnsMutation,
	useUpdateViewOverviewMutation,
	useUpdateViewSourceItemsMutation,
	useUpdateViewSourcesDataMutation,
} from '@/rtkApi/mddApiV2'
import { selectTabInTab } from '@/store/modules/tab/actions'
import { assignCollectionOrder } from '@/utils/collectionOrder'
import { logError } from '@/utils/errorHandling'

// Tab enum for local state
export enum ViewTabV2 {
	Columns = 'columns',
	Dependencies = 'dependencies',
	Deployments = 'deployments',
	Overview = 'overview',
	Permissions = 'permissions',
	Preview = 'preview',
	Sources = 'sources',
	Validation = 'validation',
	Versions = 'versions',
}

const VIEW_TAB_IDS = new Set<string>(Object.values(ViewTabV2))

/** `/v2/view/.../sources/{sourceItemId}` uses UUID path vars (same pattern as mapping DELETE). */
const SOURCE_ITEM_UUID_RE =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function isSourceItemPathUuid(value: string): boolean {
	return SOURCE_ITEM_UUID_RE.test(value.trim())
}

/** Identity for diffing: uuid first (matches DELETE); fallback to id for rows without uuid. */
function sourceItemIdentityKey(s: SourceItem): string | null {
	const u = s.uuid?.trim()
	if (u) return `u:${u}`
	if (s.id != null && s.id !== -1) return `i:${String(s.id)}`
	return null
}

function toViewTabV2(tab: string | null | undefined): ViewTabV2 | null {
	if (tab == null || !VIEW_TAB_IDS.has(tab)) return null
	return tab as ViewTabV2
}

// Form type for backwards compatibility with existing components
export interface ViewFormData {
	businessDomainId?: number,
	businessDomainName?: string,
	code?: string,
	// Columns
	columns: ViewColumn[],
	comment?: string,
	criteriaDescription?: string,
	criteriaScript?: string,
	// Custom attributes
	customAttributes?: unknown[],
	description?: string,
	// Overview fields
	name?: string,
	// Permissions
	objectPermissions: ViewPermission[],
	objectSettings?: ObjectSettings[]
	ownerId?: number,
	ownerName?: string,
	responsibleAnalystId?: number,
	responsibleAnalystName?: string,
	responsiblePersonId?: number,
	responsiblePersonName?: string,
	sourceDescription?: string,
	// Sources
	sourceItems: SourceItem[],
	sourceScript?: string,
	status?: Status,
	stereotypeId?: number,
	stereotypeName?: string
}

/**
 * V2 Hook using RTK Query endpoints for View data management.
 *
 * Features:
 * - Separate queries per tab for better performance
 * - Optimistic updates with RTK Query cache
 * - Granular mutations instead of saving entire form
 * - Local dirty state tracking
 */
export const useViewDataV2 = () => {
	const dispatch = useAppDispatch()
	const viewRefreshSignal = useAppStore((state) => state.aiSlice.viewRefreshSignal)
	const {
		state: { node, editMode, systemNodeId, selectedTab: tabFromStore },
	} = useDetailTabContext()

	const structureId = node.id
	const { selectedBranchId } = useSelectedBranch()
	const branchId = selectedBranchId ? Number(selectedBranchId) : undefined
	const skipViewQueries =
		!structureId || branchId == null || Number.isNaN(branchId)
	const viewArg = useMemo(
		() => ({
			branchId: branchId ?? -1,
			structureId: structureId ?? -1,
		}),
		[branchId, structureId],
	)

	// MDD-2605: selected tab from Redux (per opened tab); when user switches tab we dispatch selectTabInTab
	const selectedTab = toViewTabV2(tabFromStore ?? null) ?? ViewTabV2.Overview

	// === RTK Query Hooks ===

	// Full view (for fallback/initial data) — V2 GET /v2/branch/{branchId}/view/{id}
	const {
		data: fullView,
		isLoading: isViewLoading,
		refetch: refetchView,
	} = useGetStructureDetailQuery(viewArg, { skip: skipViewQueries })

	// Overview data
	const {
		data: overview,
		isLoading: isOverviewLoading,
		refetch: refetchOverview,
	} = useGetViewOverviewQuery(viewArg, { skip: skipViewQueries })

	// Columns data
	const {
		data: columns,
		isLoading: isColumnsLoading,
		refetch: refetchColumns,
	} = useGetViewColumnsQuery(viewArg, { skip: skipViewQueries })

	// Sources data
	const {
		data: sourceItems,
		isLoading: isSourcesLoading,
		refetch: refetchSources,
	} = useGetViewSourcesQuery(viewArg, { skip: skipViewQueries })

	// Sources scripts (criteria, source script)
	const {
		data: sourcesData,
		isLoading: isSourcesDataLoading,
		refetch: refetchSourcesData,
	} = useGetViewSourcesDataQuery(viewArg, { skip: skipViewQueries })

	// Permissions data
	const {
		data: permissions,
		isLoading: isPermissionsLoading,
		refetch: refetchPermissions,
	} = useGetViewPermissionsQuery(viewArg, { skip: skipViewQueries })

	// === Mutations ===

	// Overview mutations
	const [updateOverview, { isLoading: isSavingOverview }] =
		useUpdateViewOverviewMutation()

	// Columns mutations
	const [updateColumns, { isLoading: isSavingColumns }] =
		useUpdateViewColumnsMutation()
	const [addColumn] = useAddViewColumnMutation()
	const [deleteColumn] = useUpdateViewColumn1Mutation()

	// Sources mutations
	const [updateSourceItems, { isLoading: isSavingSources }] =
		useUpdateViewSourceItemsMutation()
	const [addSourceItem] = useAddViewSourceItemMutation()
	const [deleteSourceItem] = useDeleteViewSourceItemMutation()
	const [updateSourcesData, { isLoading: isSavingSourcesData }] =
		useUpdateViewSourcesDataMutation()

	// Permissions mutations
	const [setPermission, { isLoading: isSavingPermission }] =
		useSetViewPermissionMutation()
	const [deletePermission] = useDeleteViewPermissionMutation()

	// === Local State ===
	const [localOverview, setLocalOverview] = useState<ViewOverview | null>(null)
	const [localColumns, setLocalColumns] = useState<ViewColumn[] | null>(null)
	const [localSourceItems, setLocalSourceItems] = useState<SourceItem[] | null>(null)
	const [localSourcesData, setLocalSourcesData] = useState<ViewSourcesData | null>(null)
	const [localPermissions, setLocalPermissions] = useState<ViewPermission[] | null>(null)
	const [dirty, setDirty] = useState(false)

	// Track which sections have been modified (for granular V2 saves)
	// State values trigger re-renders, refs are used in save logic
	const [_dirtyOverview, setDirtyOverview] = useState(false)
	const [_dirtyColumns, setDirtyColumns] = useState(false)
	const [_dirtySources, setDirtySources] = useState(false)
	const [_dirtySourcesData, setDirtySourcesData] = useState(false)
	const [_dirtyPermissions, setDirtyPermissions] = useState(false)
	const [_dirtyExtendedFields, setDirtyExtendedFields] = useState(false)

	// Refs for dirty flags to avoid stale closures
	const dirtyOverviewRef = useRef(false)
	const dirtyColumnsRef = useRef(false)
	const dirtySourcesRef = useRef(false)
	const dirtySourcesDataRef = useRef(false)
	const dirtyPermissionsRef = useRef(false)
	const dirtyExtendedFieldsRef = useRef(false)

	/** Bumped on each edit so ElementDetailV2 debounced autosave restarts from the last change */
	const [autosaveResetKey, setAutosaveResetKey] = useState(0)
	/** Bumped when server data is reloaded (AI sync, cancel) so read-mode forms re-sync. */
	const [serverDataRevision, setServerDataRevision] = useState(0)
	const bumpAutosaveDebounce = useCallback(() => {
		setAutosaveResetKey((k) => k + 1)
	}, [])
	
	// Extra fields stored locally for stereotype/businessDomain
	const [localStereotypeId, setLocalStereotypeId] = useState<number | null>(null)
	const [localStereotypeName, setLocalStereotypeName] = useState<string | null>(null)
	const [localBusinessDomainId, setLocalBusinessDomainId] = useState<number | null>(null)
	const [localBusinessDomainName, setLocalBusinessDomainName] = useState<string | null>(null)

	// === Refs to avoid stale closures in save handler ===
	const localOverviewRef = useRef(localOverview)
	const localColumnsRef = useRef(localColumns)
	const localSourceItemsRef = useRef(localSourceItems)
	const localSourcesDataRef = useRef(localSourcesData)
	const localPermissionsRef = useRef(localPermissions)
	const localStereotypeIdRef = useRef(localStereotypeId)
	const localBusinessDomainIdRef = useRef(localBusinessDomainId)

	// Keep refs in sync with state
	useEffect(() => { localOverviewRef.current = localOverview }, [localOverview])
	useEffect(() => { localColumnsRef.current = localColumns }, [localColumns])
	useEffect(() => { localSourceItemsRef.current = localSourceItems }, [localSourceItems])
	useEffect(() => { localSourcesDataRef.current = localSourcesData }, [localSourcesData])
	useEffect(() => { localPermissionsRef.current = localPermissions }, [localPermissions])
	useEffect(() => { localStereotypeIdRef.current = localStereotypeId }, [localStereotypeId])
	useEffect(() => { localBusinessDomainIdRef.current = localBusinessDomainId }, [localBusinessDomainId])

	// Sync local state with fetched data when entering edit mode
	useEffect(() => {
		if (editMode && overview && !localOverview) {
			setLocalOverview(overview)
		}
	}, [editMode, overview, localOverview])

	useEffect(() => {
		if (editMode && columns && !localColumns) {
			setLocalColumns(columns)
		}
	}, [editMode, columns, localColumns])

	useEffect(() => {
		if (editMode && sourceItems && !localSourceItems) {
			setLocalSourceItems(sourceItems)
		}
	}, [editMode, sourceItems, localSourceItems])

	useEffect(() => {
		if (editMode && sourcesData && !localSourcesData) {
			setLocalSourcesData(sourcesData)
		}
	}, [editMode, sourcesData, localSourcesData])

	useEffect(() => {
		if (editMode && permissions && !localPermissions) {
			setLocalPermissions(permissions)
		}
	}, [editMode, permissions, localPermissions])

	// Reset local state when switching out of edit mode
	useEffect(() => {
		if (!editMode) {
			setLocalOverview(null)
			setLocalColumns(null)
			setLocalSourceItems(null)
			setLocalSourcesData(null)
			setLocalPermissions(null)
			setLocalStereotypeId(null)
			setLocalStereotypeName(null)
			setLocalBusinessDomainId(null)
			setLocalBusinessDomainName(null)
			setDirty(false)
			setDirtyOverview(false)
			setDirtyColumns(false)
			setDirtySources(false)
			setDirtySourcesData(false)
			setDirtyPermissions(false)
			setDirtyExtendedFields(false)
			dirtyOverviewRef.current = false
			dirtyColumnsRef.current = false
			dirtySourcesRef.current = false
			dirtySourcesDataRef.current = false
			dirtyPermissionsRef.current = false
			dirtyExtendedFieldsRef.current = false
		}
	}, [editMode])

	// === Derived State ===
	const isLoading = isViewLoading || isOverviewLoading
	const isSaving =
		isSavingOverview ||
		isSavingColumns ||
		isSavingSources ||
		isSavingSourcesData ||
		isSavingPermission

	// Get current overview (local if editing, otherwise from API)
	const currentOverview = editMode ? (localOverview ?? overview) : overview
	const currentColumns = editMode ? (localColumns ?? columns) : columns
	const currentSourceItems = editMode ? (localSourceItems ?? sourceItems) : sourceItems
	const currentSourcesData = editMode ? (localSourcesData ?? sourcesData) : sourcesData
	const currentPermissions = editMode ? (localPermissions ?? permissions) : permissions

	// Combined form data (for backwards compatibility)
	const form = useMemo<ViewFormData>(
		() => ({
			// Overview fields
			name: currentOverview?.name ?? fullView?.name,
			code: currentOverview?.code ?? fullView?.code,
			description: currentOverview?.description ?? fullView?.description,
			comment: currentOverview?.comment ?? fullView?.comment,
			status: currentOverview?.status ?? fullView?.status,
			// API returns nested objects (responsiblePerson, stereotype, etc.); cast to flat form for ViewFormData
			responsiblePersonId: (currentOverview?.responsiblePerson as any)?.id ?? fullView?.responsiblePersonId,
			responsiblePersonName: (currentOverview?.responsiblePerson as any)?.fullName ?? fullView?.responsiblePersonName,
			responsibleAnalystId: (currentOverview?.responsibleAnalyst as any)?.id ?? fullView?.responsibleAnalystId,
			responsibleAnalystName: (currentOverview?.responsibleAnalyst as any)?.fullName ?? fullView?.responsibleAnalystName,
			ownerId: currentOverview?.owner?.id ?? (fullView as any)?.ownerId,
			ownerName: currentOverview?.owner?.name ?? (fullView as any)?.ownerName,
			objectSettings: currentOverview?.objectSettings ?? fullView?.objectSettings,
			stereotypeId: localStereotypeId ?? (currentOverview?.stereotype as any)?.id ?? (fullView as any)?.stereotypeId,
			stereotypeName: localStereotypeName ?? (currentOverview?.stereotype as any)?.name ?? (fullView as any)?.stereotypeName,
			businessDomainId: localBusinessDomainId ?? (fullView as any)?.businessDomainId,
			businessDomainName: localBusinessDomainName ?? (fullView as any)?.businessDomainName,
			// Columns
			columns: currentColumns ?? fullView?.columns ?? [],
			// Sources
			sourceItems: currentSourceItems ?? fullView?.sourceItems ?? [],
			sourceScript: currentSourcesData?.source?.script ?? fullView?.sourceScript ?? '',
			sourceDescription: currentSourcesData?.source?.description ?? fullView?.sourceDescription ?? '',
			criteriaScript: currentSourcesData?.criteria?.script ?? fullView?.criteriaScript ?? '',
			criteriaDescription: currentSourcesData?.criteria?.description ?? fullView?.criteriaDescription ?? '',
			// Permissions
			objectPermissions: currentPermissions ?? [],
			// Custom attributes from overview API (V2) – normalize id/version to number for hook
			customAttributes: (currentOverview?.customAttributes ?? (fullView as any)?.customAttributes ?? []).map(
				(ca: CustomAttribute & { id?: number; version?: number }) => ({
					...ca,
					id: ca.id != null ? Number(ca.id) : undefined,
					version: ca.version != null ? Number(ca.version) : undefined,
				}),
			),
		}),
		[
			currentOverview,
			fullView,
			currentColumns,
			currentSourceItems,
			currentSourcesData,
			currentPermissions,
			localStereotypeId,
			localStereotypeName,
			localBusinessDomainId,
			localBusinessDomainName,
		],
	)

	// === Change Handlers ===

	const handleOverviewChange = useCallback(
		(update: Partial<ViewOverview>) => {
			if (!editMode) return
			bumpAutosaveDebounce()
			// Initialize from API data if local state is null, then merge update
			setLocalOverview((prev) => {
				const base = prev ?? overview ?? {}
				return { ...base, ...update }
			})
			setDirty(true)
			setDirtyOverview(true)
			dirtyOverviewRef.current = true
		},
		[bumpAutosaveDebounce, editMode, overview],
	)

	const handleColumnsChange = useCallback(
		(newColumns: ViewColumn[]) => {
			if (!editMode) return
			bumpAutosaveDebounce()
			setLocalColumns(newColumns)
			setDirty(true)
			setDirtyColumns(true)
			dirtyColumnsRef.current = true
		},
		[bumpAutosaveDebounce, editMode],
	)

	const handleSourceItemsChange = useCallback(
		(newSourceItems: SourceItem[]) => {
			if (!editMode) return
			bumpAutosaveDebounce()
			// Keep ref in sync immediately — handleSave reads this ref; useEffect runs too late
			// after setState, so a save right after delete could miss removals (mapping saves full JSON; V2 uses DELETE + PUT).
			localSourceItemsRef.current = newSourceItems
			setLocalSourceItems(newSourceItems)
			setDirty(true)
			setDirtySources(true)
			dirtySourcesRef.current = true
		},
		[bumpAutosaveDebounce, editMode],
	)

	const handleSourcesDataChange = useCallback(
		(update: Partial<ViewSourcesData>) => {
			if (!editMode) return
			bumpAutosaveDebounce()
			setLocalSourcesData((prev) => ({
				...prev,
				...update,
			}))
			setDirty(true)
			setDirtySourcesData(true)
			dirtySourcesDataRef.current = true
		},
		[bumpAutosaveDebounce, editMode],
	)

	const handlePermissionsChange = useCallback(
		(newPermissions: ViewPermission[]) => {
			if (!editMode) return
			bumpAutosaveDebounce()
			setLocalPermissions(newPermissions)
			setDirty(true)
			setDirtyPermissions(true)
			dirtyPermissionsRef.current = true
		},
		[bumpAutosaveDebounce, editMode],
	)

	// Generic change handler (for backwards compatibility)
	const handleChange = useCallback(
		(update: Record<string, unknown>) => {
			if (!editMode) return

			// Route update to appropriate handler based on keys
			const keys = Object.keys(update)

			if (keys.includes('columns')) {
				handleColumnsChange(update.columns as ViewColumn[])
				return
			}

			if (keys.includes('sourceItems')) {
				handleSourceItemsChange(update.sourceItems as SourceItem[])
				return
			}

			if (keys.some(k => ['sourceScript', 'sourceDescription', 'criteriaScript', 'criteriaDescription'].includes(k))) {
				const currentSource = localSourcesData?.source ?? sourcesData?.source ?? {}
				const currentCriteria = localSourcesData?.criteria ?? sourcesData?.criteria ?? {}
				
				handleSourcesDataChange({
					source: {
						...currentSource,
						script: update.sourceScript as string ?? currentSource.script,
						description: update.sourceDescription as string ?? currentSource.description,
					},
					criteria: {
						...currentCriteria,
						script: update.criteriaScript as string ?? currentCriteria.script,
						description: update.criteriaDescription as string ?? currentCriteria.description,
					},
				})
				return
			}

			if (keys.includes('objectPermissions')) {
				handlePermissionsChange(update.objectPermissions as ViewPermission[])
				return
			}

			if (keys.includes('customAttributes')) {
				handleOverviewChange({
					customAttributes: update.customAttributes as CustomAttribute[],
				})
				return
			}

			// Assume it's an overview field - map to ViewOverview structure
			const overviewUpdate: Partial<ViewOverview> = {}
			
			if ('name' in update) overviewUpdate.name = update.name as string
			if ('code' in update) overviewUpdate.code = update.code as string
			if ('description' in update) overviewUpdate.description = update.description as string
			if ('comment' in update) overviewUpdate.comment = update.comment as string
			if ('status' in update) overviewUpdate.status = update.status as Status
			if ('objectSettings' in update) overviewUpdate.objectSettings = update.objectSettings as ObjectSettings[]
			
			// Handle user/owner fields - need to map to nested structure
			if ('responsiblePersonId' in update) {
				overviewUpdate.responsiblePerson = { id: update.responsiblePersonId as number }
			}
			if ('responsibleAnalystId' in update) {
				overviewUpdate.responsibleAnalyst = { id: update.responsibleAnalystId as number }
			}
			if ('ownerId' in update) {
				overviewUpdate.owner = { 
					id: update.ownerId as number,
					name: update.ownerName as string,
				}
			}

			// Handle fields not in ViewOverview API - store locally
			if ('stereotypeId' in update) {
				bumpAutosaveDebounce()
				setLocalStereotypeId(update.stereotypeId as number | null)
				setLocalStereotypeName(update.stereotypeName as string | null)
				setDirty(true)
				setDirtyExtendedFields(true)
				dirtyExtendedFieldsRef.current = true
			}
			if ('businessDomainId' in update) {
				bumpAutosaveDebounce()
				setLocalBusinessDomainId(update.businessDomainId as number | null)
				setLocalBusinessDomainName(update.businessDomainName as string | null)
				setDirty(true)
				setDirtyExtendedFields(true)
				dirtyExtendedFieldsRef.current = true
			}

			if (Object.keys(overviewUpdate).length > 0) {
				handleOverviewChange(overviewUpdate)
			}
		},
		[
			bumpAutosaveDebounce,
			editMode,
			handleColumnsChange,
			handleOverviewChange,
			handlePermissionsChange,
			handleSourceItemsChange,
			handleSourcesDataChange,
			localSourcesData,
			sourcesData,
		],
	)

	// === Save Handlers (individual section saves - available for future use) ===

	const _saveOverview = useCallback(async () => {
		if (!localOverview || skipViewQueries) return
		const viewOverviewPayload: ViewOverview = {
			...localOverview,
			...(localStereotypeId !== null && { stereotype: { id: localStereotypeId } }),
		}
		await updateOverview({
			...viewArg,
			viewOverview: viewOverviewPayload,
		}).unwrap()
		await refetchOverview()
	}, [localOverview, localStereotypeId, skipViewQueries, updateOverview, refetchOverview, viewArg])

	const _saveColumns = useCallback(async () => {
		if (!localColumns || skipViewQueries) return
		await updateColumns({
			...viewArg,
			body: localColumns,
		}).unwrap()
		await refetchColumns()
	}, [localColumns, skipViewQueries, updateColumns, refetchColumns, viewArg])

	const _saveSourceItems = useCallback(async () => {
		if (!localSourceItems || skipViewQueries) return
		await updateSourceItems({
			...viewArg,
			body: localSourceItems,
		}).unwrap()
		await refetchSources()
	}, [localSourceItems, skipViewQueries, updateSourceItems, refetchSources, viewArg])

	const _saveSourcesData = useCallback(async () => {
		if (!localSourcesData || skipViewQueries) return
		await updateSourcesData({
			...viewArg,
			viewSourcesData: localSourcesData,
		}).unwrap()
		await refetchSourcesData()
	}, [localSourcesData, skipViewQueries, updateSourcesData, refetchSourcesData, viewArg])

	const _savePermissions = useCallback(async () => {
		if (!localPermissions || skipViewQueries) return
		
		// Find permissions to delete (in original but not in local)
		const originalUserCodes = new Set(permissions?.map(p => p.user?.code).filter(Boolean) || [])
		const localUserCodes = new Set(localPermissions.map(p => p.user?.code).filter(Boolean))
		
		// Delete removed permissions
		const deletedUserCodes = [...originalUserCodes].filter(code => !localUserCodes.has(code))
		for (const userCode of deletedUserCodes) {
			if (userCode) {
				await deletePermission({
					...viewArg,
					userCode,
				}).unwrap()
			}
		}
		
		// Set/update remaining permissions
		for (const permission of localPermissions) {
			if (permission.user?.code) {
				await setPermission({
					...viewArg,
					viewPermission: permission,
				}).unwrap()
			}
		}
		await refetchPermissions()
	}, [localPermissions, permissions, skipViewQueries, setPermission, deletePermission, refetchPermissions, viewArg])

	// Save only modified sections using V2 API mutations.
	const handleSave = useCallback(async (opts?: ElementSaveOptions) => {
		try {
			if (skipViewQueries) return

			// Use refs for dirty flags first — skip blur/network when nothing changed
			const isDirtyOverview = dirtyOverviewRef.current
			const isDirtyColumns = dirtyColumnsRef.current
			const isDirtySources = dirtySourcesRef.current
			const isDirtySourcesData = dirtySourcesDataRef.current
			const isDirtyPermissions = dirtyPermissionsRef.current
			const isDirtyExtendedFields = dirtyExtendedFieldsRef.current

			if (
				!isDirtyOverview &&
				!isDirtyColumns &&
				!isDirtySources &&
				!isDirtySourcesData &&
				!isDirtyPermissions &&
				!isDirtyExtendedFields
			) {
				return
			}

			const shouldFlushActiveElement = opts?.flushActiveElement !== false
			const shouldRestoreFocus = shouldFlushActiveElement && !opts?.skipRefetch

			if (shouldFlushActiveElement) {
				// Flush focused cell/input into local state before reading refs.
				// Restore focus afterwards unless this is save-on-leave (user is navigating away).
				const active = document.activeElement as HTMLElement | null
				if (active && typeof active.blur === 'function') {
					active.blur()
					await new Promise((r) => setTimeout(r, 50))
					if (
						shouldRestoreFocus &&
						document.contains(active) &&
						typeof active.focus === 'function'
					) {
						active.focus()
					}
				}
			}

			// Use refs to get current values (avoids stale closure issues)
			const currentOverview = localOverviewRef.current
			const currentColumns = localColumnsRef.current
			const currentSourceItems = localSourceItemsRef.current
			const currentSourcesData = localSourcesDataRef.current
			const currentPermissions = localPermissionsRef.current
			const currentStereotypeId = localStereotypeIdRef.current
			const _currentBusinessDomainId = localBusinessDomainIdRef.current

			const promises: Promise<unknown>[] = []

			// Save overview via V2 API - only if modified (including stereotype changes)
			if ((isDirtyOverview || isDirtyExtendedFields) && (currentOverview || currentStereotypeId !== null)) {
				// Map custom attributes to API shape (id/version as string)
				const customAttributesForApi: CustomAttribute[] | undefined =
					currentOverview?.customAttributes?.map((ca: CustomAttribute & { id?: number; version?: number }) => ({
						...ca,
						id: ca.id != null ? String(ca.id) : undefined,
						version: ca.version != null ? String(ca.version) : undefined,
					}))
				const viewOverviewPayload: ViewOverview = {
					...currentOverview,
					customAttributes: customAttributesForApi,
					// Include stereotype if it was changed
					...(currentStereotypeId !== null && { stereotype: { id: currentStereotypeId } }),
				}
				promises.push(
					updateOverview({
						...viewArg,
						viewOverview: viewOverviewPayload,
					}).unwrap()
				)
			}

			// Save columns via V2 API - only if modified
			// Filter out incomplete rows - a column must have a code to be saved
			// (name alone is not enough - code is the required identifier)
			if (isDirtyColumns && currentColumns) {
				const validColumns = assignCollectionOrder(
					currentColumns.filter(c => c.code && c.code.trim() !== ''),
				)
				if (validColumns.length > 0) {
					promises.push(
						updateColumns({
							...viewArg,
							body: validColumns,
						}).unwrap()
					)
				}
			}

			// Save source items via V2 API - only if modified
			// 1) Delete removed rows first (updateSourceItems is not a full replace on BE)
			// 2) Then PUT remaining valid rows
			// A row is valid if it has ANY of these fields set:
			// - objectUuid (from dropdown selection)
			// - objectId > 0 (pre-existing reference from API)
			// - objectCode (manual entry)
			// - stereotype (source type selected)
			// - alias (user-defined alias)
			// - expression (computed source)
			if (isDirtySources && currentSourceItems) {
				const baselineSourceItems = sourceItems ?? []
				const currentIdentityKeys = new Set(
					currentSourceItems
						.map(sourceItemIdentityKey)
						.filter((k): k is string => k != null),
				)
				const deletedBaselineItems = baselineSourceItems.filter((s) => {
					const k = sourceItemIdentityKey(s)
					return k != null && !currentIdentityKeys.has(k)
				})

				for (const item of deletedBaselineItems) {
					const pathUuid = item.uuid?.trim()
					if (pathUuid && isSourceItemPathUuid(pathUuid)) {
						await deleteSourceItem({
							...viewArg,
							sourceItemId: pathUuid,
						}).unwrap()
					} else {
						logError(
							new Error(
								'Cannot DELETE view source item: backend expects UUID in path, not numeric id',
							),
							{ operation: 'useViewDataV2.save.deletedSource', details: { item } },
						)
					}
				}

				const validSourceItems = assignCollectionOrder(
					currentSourceItems
					.filter(s => 
						s.objectUuid ||
						(s.objectId && s.objectId > 0) || 
						(s.objectCode && s.objectCode.trim() !== '') ||
						s.stereotype ||
						(s.alias && s.alias.trim() !== '') ||
						(s.expression && s.expression.trim() !== '')
					)
					// Sanitize: remove invalid reference fields to prevent backend errors
					.map(s => {
						const sanitized = { ...s }
						if (sanitized.objectId !== undefined && sanitized.objectId <= 0) {
							delete sanitized.objectId
						}
						if (sanitized.objectCode !== undefined && sanitized.objectCode.trim() === '') {
							delete sanitized.objectCode
						}
						return sanitized
					}),
				)
				if (validSourceItems.length > 0) {
					promises.push(
						updateSourceItems({
							...viewArg,
							body: validSourceItems,
						}).unwrap()
					)
				}
			}

			// Save sources data (scripts) via V2 API - only if modified
			if (isDirtySourcesData && currentSourcesData) {
				promises.push(
					updateSourcesData({
						...viewArg,
						viewSourcesData: currentSourcesData,
					}).unwrap()
				)
			}

			if (promises.length > 0) {
				await Promise.all(promises)
			}

			// Save permissions via V2 API - only if modified
			// Handle permissions separately (sequentially) to avoid race conditions
			if (isDirtyPermissions && currentPermissions) {
				// Filter to only valid permissions (with user code)
				const validPermissions = assignCollectionOrder(
					currentPermissions.filter(p => p.user?.code),
				)
				
				// Delete removed permissions first (sequentially)
				const originalUserCodes = new Set(permissions?.map(p => p.user?.code).filter(Boolean) || [])
				const localUserCodes = new Set(validPermissions.map(p => p.user?.code).filter(Boolean))
				const deletedUserCodes = [...originalUserCodes].filter(code => !localUserCodes.has(code))
				
				for (const userCode of deletedUserCodes) {
					if (userCode) {
						await deletePermission({ ...viewArg, userCode }).unwrap()
					}
				}
				
				// Set/update valid permissions (sequentially to avoid race conditions)
				for (const permission of validPermissions) {
					await setPermission({ ...viewArg, viewPermission: permission }).unwrap()
				}
			}
			
			// Reset dirty flags (both state and refs)
			setDirty(false)
			setDirtyOverview(false)
			setDirtyColumns(false)
			setDirtySources(false)
			setDirtySourcesData(false)
			setDirtyPermissions(false)
			setDirtyExtendedFields(false)
			dirtyOverviewRef.current = false
			dirtyColumnsRef.current = false
			dirtySourcesRef.current = false
			dirtySourcesDataRef.current = false
			dirtyPermissionsRef.current = false
			dirtyExtendedFieldsRef.current = false
			
			// Refetch to update RTK Query cache so Redux has the saved data (e.g. when navigating back to the view).
			// Skip on leave-save to avoid "query not started" errors during unmount/navigation.
			if (!skipViewQueries && !opts?.skipRefetch) {
				const refetchPromises: Promise<unknown>[] = [refetchView()]
				if (isDirtyOverview) refetchPromises.push(refetchOverview())
				if (isDirtyColumns) refetchPromises.push(refetchColumns())
				if (isDirtySources) refetchPromises.push(refetchSources())
				if (isDirtySourcesData) refetchPromises.push(refetchSourcesData())
				if (isDirtyPermissions) refetchPromises.push(refetchPermissions())
				await Promise.all(refetchPromises)
			}

			dispatch(
				mddApi.util.invalidateTags([{ type: 'SqlPreview', id: structureId }]),
			)
		} catch (error) {
			logError(error, { operation: 'useViewDataV2.save' })
			throw error
		}
	}, [
		dispatch,
		skipViewQueries,
		permissions,
		sourceItems,
		viewArg,
		// Note: dirty flags are read from refs, not state, to avoid stale closures
		updateOverview,
		updateColumns,
		updateSourceItems,
		deleteSourceItem,
		updateSourcesData,
		setPermission,
		deletePermission,
		refetchView,
		refetchOverview,
		refetchColumns,
		refetchSources,
		refetchSourcesData,
		refetchPermissions,
	])

	const clearLocalViewEdits = useCallback(() => {
		setLocalOverview(null)
		setLocalColumns(null)
		setLocalSourceItems(null)
		setLocalSourcesData(null)
		setLocalPermissions(null)
		setLocalStereotypeId(null)
		setLocalStereotypeName(null)
		setLocalBusinessDomainId(null)
		setLocalBusinessDomainName(null)
		setDirty(false)
		setDirtyOverview(false)
		setDirtyColumns(false)
		setDirtySources(false)
		setDirtySourcesData(false)
		setDirtyPermissions(false)
		setDirtyExtendedFields(false)
		dirtyOverviewRef.current = false
		dirtyColumnsRef.current = false
		dirtySourcesRef.current = false
		dirtySourcesDataRef.current = false
		dirtyPermissionsRef.current = false
		dirtyExtendedFieldsRef.current = false
	}, [])

	const refetchAllViewQueries = useCallback(async () => {
		if (skipViewQueries) return
		await Promise.all([
			refetchView(),
			refetchOverview(),
			refetchColumns(),
			refetchSources(),
			refetchSourcesData(),
			refetchPermissions(),
		])
	}, [
		skipViewQueries,
		refetchView,
		refetchOverview,
		refetchColumns,
		refetchSources,
		refetchSourcesData,
		refetchPermissions,
	])

	const reloadViewFromServer = useCallback(async () => {
		if (!structureId) return
		dispatch(
			mddApiV2.util.invalidateTags([{ type: 'Views', id: structureId }]),
		)
		await refetchAllViewQueries()
		clearLocalViewEdits()
		setServerDataRevision((revision) => revision + 1)
	}, [clearLocalViewEdits, dispatch, refetchAllViewQueries, structureId])

	// Cancel and reset – refetch first so cache is fresh, then clear local state
	const handleCancel = useCallback(
		async (_node: StructureObjectDto) => {
			await reloadViewFromServer()
		},
		[reloadViewFromServer],
	)

	// Reload from server when AI changed this view (clears local overrides too)
	const viewRefreshNonce = viewRefreshSignal?.nodeId === structureId
		? viewRefreshSignal?.nonce
		: undefined

	useEffect(() => {
		if (viewRefreshNonce == null) return
		void reloadViewFromServer()
	}, [viewRefreshNonce, reloadViewFromServer])

	// Tab change (MDD-2605: persist to Redux so returning to this View restores the tab)
	const handleTabChange = useCallback(
		(tab: ViewTabV2) => {
			dispatch(selectTabInTab(structureId, tab))
		},
		[dispatch, structureId],
	)

	// Edit handler - called by EditableNodeActions when entering edit mode
	// In V2, the data is already loaded via RTK Query, so we just need to
	// ensure local state is synced when entering edit mode
	const handleEdit = useCallback(async (_node: StructureObjectDto) => {
		// Sync local state with fetched data when entering edit mode
		if (overview) setLocalOverview(overview)
		if (columns) setLocalColumns(columns)
		if (sourceItems) setLocalSourceItems(sourceItems)
		if (sourcesData) setLocalSourcesData(sourcesData)
		if (permissions) setLocalPermissions(permissions)
	}, [overview, columns, sourceItems, sourcesData, permissions])

	return {
		// Data
		node,
		view: fullView,
		editMode,
		systemNodeId,
		form,
		dirty,
		autosaveResetKey,
		serverDataRevision,
		selectedTab,
		objectTypes: form.objectSettings,

		// Loading states
		isLoading,
		isSaving,
		isOverviewLoading,
		isColumnsLoading,
		isSourcesLoading: isSourcesLoading || isSourcesDataLoading,
		isPermissionsLoading,

		// Raw data access
		overview: currentOverview,
		columns: currentColumns ?? [],
		sourceItems: currentSourceItems ?? [],
		sourcesData: currentSourcesData,
		permissions: currentPermissions ?? [],

		// Generic change handler (backwards compatible)
		onChange: handleChange,

		// Specific change handlers
		onOverviewChange: handleOverviewChange,
		onColumnsChange: handleColumnsChange,
		onSourceItemsChange: handleSourceItemsChange,
		onSourcesDataChange: handleSourcesDataChange,
		onPermissionsChange: handlePermissionsChange,

		// Actions
		onEdit: handleEdit,
		onSave: handleSave,
		onCancel: handleCancel,
		onTabChange: handleTabChange,

		// Mutations for granular operations
		mutations: {
			addColumn,
			deleteColumn,
			addSourceItem,
			deleteSourceItem,
			setPermission,
			deletePermission,
		},

		// Refetch functions
		refetch: {
			all: refetchView,
			overview: refetchOverview,
			columns: refetchColumns,
			sources: refetchSources,
			sourcesData: refetchSourcesData,
			permissions: refetchPermissions,
		},
	}
}

export type UseViewDataV2Return = ReturnType<typeof useViewDataV2>
